import { Alert, Box, Button, Popover, Stack, TextField, Typography } from "@mui/material";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import type { Reaction } from "@/shared/lib/feedbackReaction";

const COMMENT_MAX_LENGTH = 2000;

/** Every string this control (and the thumbs that open it) needs, supplied by the caller's
 * own i18n namespace — this module stays feature-agnostic. */
export type FeedbackLabels = {
  thumbUpAriaLabel: string;
  thumbDownAriaLabel: string;
  thumbUpTooltip: string;
  thumbDownTooltip: string;
  commentHeadingUp: string;
  commentHeadingDown: string;
  commentSubheading: string;
  commentPlaceholderUp: string;
  commentPlaceholderDown: string;
  commentAriaLabel: string;
  commentError: string;
  skip: string;
  submit: string;
};

export type FeedbackCommentSubmitResult = { ok: true } | { ok: false; message: string };

type FeedbackCommentPopoverProps = {
  anchorEl: HTMLElement | null;
  /** The reaction that opened this popover — drives the wording. */
  reaction: Reaction | null;
  existingComment: string | null;
  labels: FeedbackLabels;
  onSubmit: (comment: string) => Promise<FeedbackCommentSubmitResult>;
  onClose: () => void;
};

/** Optional follow-up to a reaction that is already persisted.
 *
 * Nothing here is saved until Submit is pressed: dismissing via Skip, Escape, or the backdrop
 * sends no request and leaves the stored reaction (and any stored comment) exactly as it was.
 * There is deliberately no auto-save or save-on-blur, which would make "skipped" and
 * "submitted" indistinguishable and would persist half-typed text.
 */
export function FeedbackCommentPopover({
  anchorEl,
  reaction,
  existingComment,
  labels,
  onSubmit,
  onClose,
}: FeedbackCommentPopoverProps) {
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const wasOpenRef = useRef(false);

  const open = anchorEl !== null;

  // Prefill on open so an already-saved comment is visible and editable rather than hidden
  // behind an empty box the user could unknowingly overwrite.
  //
  // Only on the closed -> open transition: reacting to every `existingComment` change would
  // let a landing server response or a background refetch overwrite what the user is typing.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setComment(existingComment ?? "");
      setSubmitError(null);
    }
    wasOpenRef.current = open;
  }, [open, existingComment]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const animationFrameId = requestAnimationFrame(() => {
      commentInputRef.current?.focus();
    });
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [open]);

  // Asking a question specific to the thumb gets far more usable answers than a generic
  // "add more context". Falls back to the down-vote wording only while closed.
  const isPositive = reaction === "UP";
  const heading = isPositive ? labels.commentHeadingUp : labels.commentHeadingDown;
  const placeholder = isPositive ? labels.commentPlaceholderUp : labels.commentPlaceholderDown;

  const trimmed = comment.trim();
  const isUnchanged = trimmed === (existingComment ?? "").trim();
  const canSubmit = trimmed.length > 0 && !isUnchanged && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    const result = await onSubmit(trimmed);
    setSubmitting(false);

    // Stay open on failure so the user's text survives a network blip — closing here would
    // discard it silently, and the comment was never persisted.
    if (!result.ok) {
      setSubmitError(result.message || labels.commentError);
      return;
    }
    onClose();
  }, [canSubmit, labels.commentError, onClose, onSubmit, trimmed]);

  // This popover renders in a portal, but React still bubbles its events up the *component*
  // tree — into the host card, which may itself be a button that opens a detail view and
  // treats Space/Enter as a click. Without these, typing in the box would navigate away.
  const handleContainerClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  const handleContainerKeyDown = useCallback((event: KeyboardEvent<HTMLElement>) => {
    event.stopPropagation();
  }, []);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      onClick={handleContainerClick}
      onKeyDown={handleContainerKeyDown}
      slotProps={{
        paper: {
          sx: {
            mt: 0.5,
            p: 2,
            width: 320,
            bgcolor: "var(--theme-surface)",
            border: "1px solid var(--theme-border)",
            borderRadius: 1.5,
          },
        },
      }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="subtitle2" fontWeight="bold">
            {heading}
          </Typography>
          <Typography variant="caption" sx={{ color: "var(--theme-text-secondary)" }}>
            {labels.commentSubheading}
          </Typography>
        </Box>

        <TextField
          inputRef={commentInputRef}
          multiline
          minRows={3}
          maxRows={6}
          size="small"
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder={placeholder}
          slotProps={{
            htmlInput: {
              maxLength: COMMENT_MAX_LENGTH,
              "aria-label": labels.commentAriaLabel,
            },
          }}
        />

        {submitError ? (
          <Alert severity="error" onClose={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        ) : null}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={onClose} sx={{ color: "var(--theme-text-secondary)" }}>
            {labels.skip}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!canSubmit}
            onClick={() => void handleSubmit()}
          >
            {labels.submit}
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
}
